import * as React from 'react';
import styled from 'styled-components';
import { Badge, Box, Icon, Mixins, Text } from '@openfin/ui-library';

import { AppLogo } from '../AppLogo/AppLogo';
import { PanelButton } from '../../containers/NotificationCenterApp/PanelButton';

export interface HeaderProps {
  badgeCount?: number;
  draggable?: boolean;
  children?: React.ReactNode;
  /**
   * Header icon.
   */
  icon?: string;
  onBack?: () => void;
  onClose?: () => void;
  title?: string;
}

export const Header = ({
  badgeCount = 0,
  children,
  draggable = false,
  icon,
  onBack,
  onClose,
  title,
}: HeaderProps): JSX.Element => {
  return (
    <HeaderContainer windowDrag={draggable}>
      <HeaderInner justifyContent="space-between" alignItems="center">
        <Box justifyContent="center" alignItems="center">
          {onBack && (
            <PanelButton className="backButton" autoFocus aria-label="back" onClick={onBack}>
              <Icon icon="ArrowLeftIcon" />
            </PanelButton>
          )}
          {icon && <AppLogo size="xlarge" src={icon} aria-label="logo" alt={title ?? ''} />}
          <Title as="h1" size="large" weight="bold">
            {title}
          </Title>
        </Box>
        {badgeCount > 0 && <Badge count={badgeCount} className="badge" max={99} />}
        <SkipLink
          href="#notification-panel-view"
          aria-label={`${badgeCount} notifications - skip to content`}
        />
        <ActionsContainer justifyContent="center" alignItems="center">
          {children}
          {onClose && (
            <PanelButton onClick={onClose} className="closeButton" aria-label="close">
              <Icon icon="Cross1Icon" />
            </PanelButton>
          )}
        </ActionsContainer>
      </HeaderInner>
    </HeaderContainer>
  );
};

const SkipLink = styled.a`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  list-style: none;
`;

const ActionsContainer = styled(Box)`
  margin-left: ${({ theme }) => theme.px.small};
`;

const HeaderContainer = styled(Box)<{ windowDrag?: boolean }>`
  height: ${(props) => props.theme.px.xxxxlarge};
  padding: 0 ${({ theme }) => theme.px.large};
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.palette.background4};
  user-select: none;
  -webkit-app-region: ${(props) => (props.windowDrag ? 'drag' : 'no-drag')};

  ${Icon} {
    // prevent icon from squishing when title text is too long.
    min-width: ${(props) => props.theme.px.xlarge};
  }

  .badge {
    margin-left: ${({ theme }) => theme.px.base};
    margin-right: auto;
  }

  .backButton,
  ${ActionsContainer} > * {
    margin-right: ${({ theme }) => theme.px.small};
    -webkit-app-region: no-drag;
    cursor: pointer;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const HeaderInner = styled(Box)`
  height: 100%;
  width: 100%;
`;

const Title = styled(Text)`
  ${Mixins.textOverflow}
`;
